/* tslint:disable:import-spacing no-inferrable-types variable-name align no-trailing-whitespace */

import {Scenario}          from '@/models/scenario/scenario';
import {Logger}            from '@/util/logger';

import {ViewMemento}       from './view.memento';

import {ViewerInterface}   from './viewer.interface';
import {AxisInterface}     from './axis.interface';

import {TimeCursorManager} from './time.cursor.manager';
import {TimeCursor}        from './time.cursor';

import * as $ from 'jquery';
import {Observable} from "rxjs";

export enum TimeScaleMode {
  AUTO     = 0,
  MINUTE   = 1,
  MINUTE5  = 2,
  MINUTE15 = 3,
  HALFHOUR = 4,
  HOUR     = 5,
  HOUR2    = 6,
  HOUR4    = 7,
  HOUR6    = 8,
  HOUR12   = 9,
  DAY      = 10,
  DAY2     = 11,
  WEEK     = 12,
  WEEK2    = 13,
  MONTH    = 14,
  YEAR     = 15
}

/**
 *
 * @class TimeAxis
 *
 * @brief teh X-axis for the main viewer
 *
 * - - - - - - - - - - - - - - - - - - -
 * @author    Michael Garvin (mike.garvin@ironroad.ca)
 * @date      November 2019
 *
 */
export class TimeAxis implements AxisInterface {

  /** @var _scale TimeScaleMode the current time resolution we are using */
  private _scale: TimeScaleMode          = TimeScaleMode.AUTO;

  /** @var _nightMode boolean are we in dark mode display? */
  private _nightMode: boolean            = true;

  /** @var _scenario Scenario the current data model */
  private   _scenario: Scenario          = null;

  /** @var _viewer ViewerInterface the main viewer */
  private     _viewer: ViewerInterface   = null;

  /** @var canvas HTMLCanvasElement the canvas we draw on */
  private     _canvas: HTMLCanvasElement = null;

  /** @var _width number the current width of the widget */
  private      _width: number            = 0;

  /** @var _height number the current height of the widget */
  private     _height: number            = 0;

  private    _cursors: TimeCursorManager = null;

  private      _prefs: any               = {

    axis: {

      night: {
        fontSize: 12,
        tickColor: '#ffeb78',
        fontColor: '#ffffff'
      },

      day: {
        fontSize: 12,
        tickColor: '#5f7589',
        fontColor: '#000000'
      }
    }

  };

  /** @brief provide the DOM element ID of the canvas element */
  constructor(id: string, timeId: string, view: ViewerInterface) {

    this._canvas = document.getElementById(id) as HTMLCanvasElement;
    this._width  = $('#' + id).width();
    this._height = $('#' + id).height();
    this._viewer = view;

    this._canvas.width  = this._width;
    this._canvas.height = this._height;

    this.clear();

    this._cursors = new TimeCursorManager(timeId, this._viewer);

    /* add in scroll behavior (which means re-scale) */

    $(this._canvas).off('wheel');
    $(this._canvas).on('wheel', (event) => {

      event.preventDefault();

      let vm: ViewMemento = this._viewer.viewMemento();

      if (vm == null) {

        /* nothing to do */

        return ;
      }

      /* before we scale try to find what x (time) position is under the cursor */

      // @ts-ignore
      const st: number  = event.originalEvent.offsetX;
      let x0            = Math.min(vm._screenGraph.max.x, vm._screenGraph.min.x);
      let x1            = Math.max(vm._screenGraph.max.x, vm._screenGraph.min.x);


      x0 = x0 < 0           ? 0           : x0;
      x1 = x1 > this._width ? this._width : x1;

      if ((x0  === Number.NEGATIVE_INFINITY) ||
        (x0  === Number.POSITIVE_INFINITY) ||
        isNaN(x0) ||
        (x1  === Number.NEGATIVE_INFINITY) ||
        (x1  === Number.POSITIVE_INFINITY) ||
        isNaN(x1) ||
        (x0 === x1)) {

        /* no bounds yet */

        return ;
      }

      /* if both are offscreen, we don't have to draw anything */

      if (((x0 < 0) || (x0 > this._width)) && ((x1 < 0) || (x1 > this._width))) {

        /* nothing to draw */

        return ;
      }

      /*
       * give the end of simulation a little buffer room so they can wheel up or down without
       * having to be pixel perfect on the last second of the  simulation.
       *
       */
      if ((st < x0 ) || (st > (x1 + 60))) {

        /* not currently over the axis */

        return ;
      }

      /* wt is the current time value (hours) under the cursor, where we want to come back to */

      const sp: number    = st;
      let wt = this._viewer.screenToWorld(sp, 0)[0];

      wt /= vm._timeScale; /* adjust for existing axis scaling */

      /*
       * @note this is going to be called on each 'tick' of the wheel, so if you spin 4 ticks,
       * we will be re-scaling at each one, not all at once when you lift your finger up.
       *
       */

      // @ts-ignore
      const delta: number      = event.originalEvent.deltaY;
      let   scale: number      = Math.floor(vm._timeScale);

      if (delta > 0) {

        /* scale down */

        scale--;

        this._viewer.scaleTimeTo(scale);

      } else {

        /* scale up */

        scale++;

        this._viewer.scaleTimeTo(scale);
      }

      /* now try to move the camera so 'wt' at the new scale, is back under the cursor */

      vm                = this._viewer.viewMemento();

      const  ct: number = wt * vm._timeScale;
      const sp2: number = this._viewer.worldToScreen(ct, 0)[0];

      const  sd: number  = sp2 - sp;
      const  t1: number  = this._viewer.screenToWorld(0, 0)[0];
      const  t2: number  = this._viewer.screenToWorld(sd, 0)[0];

      /*
       * tDelta is the amount in the world space we have to adjust the camera by "bring it back"
       * to where we were.
       *
       */

      const tDelta: number = t2 - t1;

      this._viewer.panX(tDelta);

    });
  }

  /** fetch the limit cursor observable */
  limit(): Observable<TimeCursor> {

    return this._cursors.limit;
  }

  /** @brief push the graph up so that world 0 is just above the axis */
  autoPan() {

  }

  /**
   * DEPRECATED - the panzoom controller is used now in ThreeStringViewer
   *
   * time axis just gets set to 15X, its a reasonable zoom level for most monitors
   *
   */
  autoFit() {

    Logger.debug('[time] scaling time axis to 15.0');

    this._viewer.scaleTimeTo(15.0);
  }

  /** @brief clear the canvas */
  clear(): void {

    this._canvas.getContext('2d').clearRect(0, 0, this._width, this._height);
  }

  /** @brief fetch the screen width of this axis */
  width(): number {

    return this._width;
  }

  /** @brief fetch the screen height of this axis */
  height(): number {
    return this._height;
  }

  /** @brief start with a new scenario */
  setScenario(scenario: Scenario): void {

    this._scenario = scenario;
  }

  /** @brief helper to convert any granularity down to seconds */
  xPosToSeconds(pos: number, tag: string) {

    let psec: number = 0;

    switch (tag) {
      case 'min':      psec = pos * 60;                   break;
      case 'min5':     psec = pos * 60 * 5;               break;
      case 'min15':    psec = pos * 60 * 15;              break;
      case 'halfhour': psec = pos * 60 * 30;              break;
      case 'hour':     psec = pos * 60 * 60;              break;
      case 'hour2':    psec = pos * 60 * 60 * 2;          break;
      case 'hour4':    psec = pos * 60 * 60 * 4;          break;
      case 'hour6':    psec = pos * 60 * 60 * 6;          break;
      case 'hour12':   psec = pos * 60 * 60 * 12;         break;
      case 'day':      psec = pos * 60 * 60 * 24;         break;
      case 'day2':     psec = pos * 60 * 60 * 24 * 2;     break;
      case 'week':     psec = pos * 60 * 60 * 24 * 7;     break;
      case 'week2':    psec = pos * 60 * 60 * 24 * 7 * 2; break;
      case 'month':    psec = pos * 60 * 60 * 24 * 7 * 4; break;
    }

    return psec;
  }

  /** @brief force the time scale mode; use 'auto' to just do auto selection */
  setScale(tag: string) {

    const t: string = tag.trim().toLowerCase();

    this._scale = TimeScaleMode.AUTO;

    Logger.debug('[time]', 'switching scale: ', t);

    switch (t) {

      case 'min':      this._scale = TimeScaleMode.MINUTE;   break;
      case 'min5':     this._scale = TimeScaleMode.MINUTE5;  break;
      case 'min15':    this._scale = TimeScaleMode.MINUTE15; break;
      case 'halfhour': this._scale = TimeScaleMode.HALFHOUR; break;
      case 'hour':     this._scale = TimeScaleMode.HOUR;     break;
      case 'hour2':    this._scale = TimeScaleMode.HOUR2;    break;
      case 'hour4':    this._scale = TimeScaleMode.HOUR4;    break;
      case 'hour6':    this._scale = TimeScaleMode.HOUR6;    break;
      case 'hour12':   this._scale = TimeScaleMode.HOUR12;   break;
      case 'day':      this._scale = TimeScaleMode.DAY;      break;
      case 'day2':     this._scale = TimeScaleMode.DAY2;     break;
      case 'week':     this._scale = TimeScaleMode.WEEK;     break;
      case 'week2':    this._scale = TimeScaleMode.WEEK2;    break;
      case 'month':    this._scale = TimeScaleMode.MONTH;    break;

    }

  }

  /** @brief main view has changed in some way, update axis */
  update(): void {

    this.clear();

    const vm: ViewMemento = this._viewer.viewMemento();

    if (vm == null) {

      /* nothing to do */

      Logger.warning(`[time] update - no memento.`);

      return ;
    }

    const left = parseInt($(this._canvas).css('left'), 10);
    let x0     = Math.min(vm._screenGraph.max.x, vm._screenGraph.min.x);
    let x1     = Math.max(vm._screenGraph.max.x, vm._screenGraph.min.x);

    x0 -= left;
    x1 -= left;

    x0 = x0 < 0           ? 0           : x0;
    x1 = x1 > this._width ? this._width : x1;

    if ((x0  === Number.NEGATIVE_INFINITY) ||
      (x0  === Number.POSITIVE_INFINITY) ||
      isNaN(x0) ||
      (x1  === Number.NEGATIVE_INFINITY) ||
      (x1  === Number.POSITIVE_INFINITY) ||
      isNaN(x1) ||
      (x0 === x1)) {

      /* no bounds yet */

      Logger.warning(`[time] update - no bounds yet.`);

      return ;
    }

    /* if both are offscreen, we don't have to draw anything */

    if (((x0 < 0) || (x0 > this._width)) && ((x1 < 0) || (x1 > this._width))) {

      /* nothing to draw */

      Logger.debug(`[time] update - off screen.`);

      return ;
    }

    /* we need to draw whatever is between the project of y0 - y1 */

    // Logger.debug('[time]', 'updating...');

    const context = this._canvas.getContext('2d');

    /* debug */

    if (false) {

      context.strokeStyle = '#ffffff';
      context.lineWidth = 2;

      context.beginPath();
      context.moveTo(x0, 0);
      context.lineTo(x0, this._height);
      context.stroke();

      context.beginPath();
      context.moveTo(x1, 0);
      context.lineTo(x1, this._height);
      context.stroke();
    }

    /* get the start/end values in world space */

    const r0x    = this._viewer.screenToWorld(x0 + left, 0)[0];
    const r1x    = this._viewer.screenToWorld(x1 + left, 0)[0];

    /* how much *could* be shown on screen (above axis) with current zoom and scaling? */

    const wx0    = this._viewer.screenToWorld(left, 0)[0];
    const wx1    = this._viewer.screenToWorld(this._width + left, 0)[0];

    const width  = Math.abs(wx1 - wx0) / vm._timeScale * 3600;

    /* parameter setup, remember, canvas Y is inverted */

    const     fs: number = this._prefs.axis.day.fontSize;
    const  yLine: number = this._height - (8 + fs + 8 + fs + 4 + 10); /* leave room for two lines of text */
    const yMajor: number = this._height - (8);                        /* the larger scale */
    const yMinor: number = this._height - (8 + fs + 8);               /* the smaller scale */

    /*
     * before we can start, we need to figure out the auto-scale, in case no explicit scale is set.
     * auto-scaling is based on how much view port we have, not where the graph happens to be
     * translated to. Zoom level is accounted for by projecting the viewport into world space,
     * to see how much time we could show, if the graph was perfectly fitted to the screen, at its
     * current zoom level.
     *
     */

    const spacers: {
      span: number,
      minor: TimeScaleMode,
      major: TimeScaleMode,
      tag: string,
      r0x: number,
      r1x: number}[] = [
      {
        span: 60,
        minor: TimeScaleMode.MINUTE,
        major: TimeScaleMode.HALFHOUR,
        tag: 'min',
        r0x: r0x * 60 / vm._timeScale,
        r1x: r1x * 60 / vm._timeScale
      },
      {
        span: 5 * 60,
        minor: TimeScaleMode.MINUTE5,
        major: TimeScaleMode.HALFHOUR,
        tag: 'min5',
        r0x: r0x * 12 / vm._timeScale,
        r1x: r1x * 12 / vm._timeScale
      },
      {
        span: 15 * 60,
        minor: TimeScaleMode.MINUTE15,
        major: TimeScaleMode.HOUR,
        tag: 'min15',
        r0x: r0x * 4 / vm._timeScale,
        r1x: r1x * 4 / vm._timeScale
      },
      {
        span: 60 * 30,
        minor: TimeScaleMode.HALFHOUR,
        major: TimeScaleMode.HOUR,
        tag: 'halfhour',
        r0x: r0x * 2 / vm._timeScale,
        r1x: r1x * 2 / vm._timeScale
      },
      {
        span: 60 * 60,
        minor: TimeScaleMode.HOUR,
        major: TimeScaleMode.DAY,
        tag: 'hour',
        r0x: r0x / vm._timeScale,
        r1x: r1x / vm._timeScale
      },
      {
        span: 60 * 60 * 2,
        minor: TimeScaleMode.HOUR2,
        major: TimeScaleMode.DAY,
        tag: 'hour2',
        r0x: r0x / 2 / vm._timeScale,
        r1x: r1x / 2 / vm._timeScale
      },
      {
        span: 60 * 60 * 4,
        minor: TimeScaleMode.HOUR4,
        major: TimeScaleMode.DAY,
        tag: 'hour4',
        r0x: r0x / 4 / vm._timeScale,
        r1x: r1x / 4 / vm._timeScale
      },
      {
        span: 60 * 60 * 6,
        minor: TimeScaleMode.HOUR6,
        major: TimeScaleMode.DAY,
        tag: 'hour6',
        r0x: r0x / 6 / vm._timeScale,
        r1x: r1x / 6 / vm._timeScale
      },
      {
        span: 60 * 60 * 12,
        minor: TimeScaleMode.HOUR12,
        major: TimeScaleMode.DAY,
        tag: 'hour12',
        r0x: r0x / 12 / vm._timeScale,
        r1x: r1x / 12 / vm._timeScale
      },
      {
        span: 60 * 60 * 24,
        minor: TimeScaleMode.DAY,
        major: TimeScaleMode.WEEK,
        tag: 'day',
        r0x: r0x / 24.0 / vm._timeScale,
        r1x: r1x / 24.0 / vm._timeScale
      },
      {
        span: 60 * 60 * 24 * 2,
        minor: TimeScaleMode.DAY2,
        major: TimeScaleMode.WEEK,
        tag: 'day2',
        r0x: r0x / 48.0 / vm._timeScale,
        r1x: r1x / 48.0 / vm._timeScale
      },
      {
        span: 60 * 60 * 24 * 7,
        minor: TimeScaleMode.WEEK,
        major: TimeScaleMode.MONTH,
        tag: 'week',
        r0x: r0x / (24.0 * 7) / vm._timeScale,
        r1x: r1x / (24.0 * 7) / vm._timeScale
      },
      {
        span: 60 * 60 * 24 * 7 * 2,
        minor: TimeScaleMode.WEEK2,
        major: TimeScaleMode.MONTH,
        tag: 'week2',
        r0x: r0x / (24.0 * 7 * 2) / vm._timeScale,
        r1x: r1x / (24.0 * 7 * 2) / vm._timeScale
      },
      {
        span: 60 * 60 * 24 * 7 * 4,
        minor: TimeScaleMode.MONTH,
        major: TimeScaleMode.YEAR,
        tag: 'month',
        r0x: r0x / (24.0 * 7 * 4) / vm._timeScale,
        r1x: r1x / (24.0 * 7 * 4) / vm._timeScale
      }
    ];

    let space: {
      span: number,
      minor: TimeScaleMode,
      major: TimeScaleMode,
      tag: string,
      r0x: number,
      r1x: number
    } = null;

    if (this._scale === TimeScaleMode.AUTO) {

      /* look for the closest range we can support for this viewport width */

      for (let i = 0; i < spacers.length; i++) {

        if (spacers[i].span > width) {

          space = (i > 1) ? spacers[i - 2] : spacers[0];
          break;
        }

      }

    } else {

      space = spacers.filter(s => s.minor === this._scale)[0];
    }

    if (space == null) {

      space = spacers[spacers.length - 1];
    }


    /* now we have the granularity to show, at both our minor and major tick levels.  Time to draw axis... */

    /* minor ticks */

    {

      /* at the minute level, tick ever 5min */

      const delta: number    = 1;
      const scale: number    = (x1 - x0) / ((space.r1x - space.r0x));
      const start: number    = ((Math.floor((space.r0x) / delta) * delta) - delta);
      const   end: number    = ((Math.floor((space.r1x) / delta) * delta) + delta);
      let    xOff: number    = 0;
      let   dummy: number    = 0;
      let    xMin: number    = x1;
      let    xMax: number    = 0;
      let      dx: number    = 0;

      for (let pos = start; pos <= end; pos += delta) {

        const psec: number = this.xPosToSeconds(pos, space.tag);

        if (pos === start) {

          /* get the screen project of the workspace first position */

          [xOff, dummy] = this._viewer.worldToScreen(psec / 3600.0 * vm._timeScale, 0);

        }

        const x: number = (pos * scale) - (start * scale) + xOff - left;

        if (dx === 0) {

          const dsec: number = this.xPosToSeconds(pos, space.tag);

          [xOff, dummy] = this._viewer.worldToScreen(dsec / 3600.0 * vm._timeScale, 0);

          dx = ((pos + delta) * scale) - (start * scale) + xOff - left - x;
        }

        if (pos < 0) {

          /* we don't have to draw anything before 0, time didn't exist :) */

          continue;
        }

        if (x < xMin) {

          xMin = x;
        }

        if (x > xMax) {

          xMax = x;
        }

        /* figure out the tick label ... */

        const timeofday: string = new Date(psec * 1000).toISOString().substr(11, 8);
        const       day: string = Math.floor(psec / 86400).toString().padStart(2, '0');
        const     label: string = 'D ' + day + ' ' + timeofday;

        // Logger.debug('[time]', 'tick - ' + label + ':', x);

        /* draw the tick */

        if (this._nightMode) {

          context.strokeStyle = this._prefs.axis.night.tickColor;
          context.fillStyle   = this._prefs.axis.night.tickColor;

        } else {

          context.strokeStyle = this._prefs.axis.day.tickColor;
          context.fillStyle   = this._prefs.axis.day.tickColor;
        }

        context.lineWidth   = 1;

        context.beginPath();
        context.moveTo(x, yMinor - 4);
        context.lineTo(x, yLine);
        context.stroke();

        /*
         * draw "ruler" marks, between here and the next tick; we have some custom rules for
         * how we draw the minor ticks:
         *
         *  - If distance between major tick marks >= 1 day: show 12 Minor tick marks between major.
         *  - If distance between major tick marks <1 day but >=6 hours: show 6 minor tick marks between major
         *  - If distance between major tick marks < 6 hours but >30 minutes: show 4 minor tick marks between major
         *  - If distance between major tick marks <= 30 minutes: show 3 minor tick marks between major
         *
         */

        let markN: number = 10;

        switch (space.minor) {

          case TimeScaleMode.MINUTE:
          case TimeScaleMode.MINUTE5:
          case TimeScaleMode.MINUTE15:
          case TimeScaleMode.HALFHOUR: {

              /* show 3 minor ticks */

              markN = 3;

            }
            break;

          case TimeScaleMode.HOUR:
          case TimeScaleMode.HOUR2:
          case TimeScaleMode.HOUR4:
          case TimeScaleMode.HOUR6: {

              /* show 4 minor ticks */

              markN = 4;

            }
            break;

          case TimeScaleMode.HOUR12: {

              /* show 6 minor ticks */

              markN = 6;

            }
            break;

          case TimeScaleMode.DAY: {

              /* show 12 minor ticks */

              markN = 12;

            }
            break;

          case TimeScaleMode.DAY2: {

              /* show a minor tick for each day */

              markN = 2;

            }
            break;

          case TimeScaleMode.WEEK: {

              /* show a minor tick for each day */

              markN = 7;
            }
            break;

          case TimeScaleMode.WEEK2: {

              /* show a minor tick for each day */

              markN = 14;
            }
            break;

          case TimeScaleMode.MONTH: {

              /* show a minor tick for each week */

              markN = 4;
            }
            break;

          case TimeScaleMode.YEAR: {

              /* show a minor tick for each month */

              markN = 12;
            }
            break;
        }

        const markSpace: number = dx / markN;

        for (let n: number = 1; n < markN; n++) {

          context.beginPath();

          context.moveTo(x + (markSpace * n), yLine + 5);
          context.lineTo(x + (markSpace * n), yLine);

          context.stroke();
        }

        /* place text just beside the major tick */

        context.font          = 'normal bold ' + fs + 'px Verdana';
        context.textBaseline  = 'middle';

        if (this._nightMode) {

          context.strokeStyle = this._prefs.axis.night.fontColor;
          context.fillStyle   = this._prefs.axis.night.fontColor;

        } else {

          context.strokeStyle = this._prefs.axis.day.fontColor;
          context.fillStyle   = this._prefs.axis.day.fontColor;
        }

        context.textBaseline  = 'bottom';
        context.textAlign     = 'left';

        context.fillText(label, x + 4, yMinor - 4);

      } /* end of minor axis walk */

      /* draw the axis line */

      context.font          = 'normal bold ' + fs + 'px Verdana';
      context.textBaseline  = 'middle';

      if (this._nightMode) {

        context.strokeStyle = this._prefs.axis.night.tickColor;
        context.fillStyle   = this._prefs.axis.night.tickColor;

      } else {

        context.strokeStyle = this._prefs.axis.day.tickColor;
        context.fillStyle   = this._prefs.axis.day.tickColor;
      }

      context.lineWidth     = 1;

      context.beginPath();
      context.moveTo(xMin, yLine);
      context.lineTo(xMax + (delta * scale), yLine);
      context.stroke();

    } /* end of minor ticks */

    /* major ticks */

    {
      const matches = spacers.filter(s => s.minor === space.major);

      if (matches.length > 0) {

        const major = matches[0];

        /* we just need to show the value label its tick locations ... */

        const delta: number = 1;
        const scale: number = (x1 - x0) / ((major.r1x - major.r0x));
        const start: number = ((Math.floor(major.r0x / delta) * delta) - delta) * vm._timeScale;
        const   end: number = ((Math.floor(major.r1x / delta) * delta) + delta) * vm._timeScale;
        let    xOff: number = 0;
        let   dummy: number = 0;

        for (let pos = start; pos <= end; pos += delta) {

          const psec: number = this.xPosToSeconds(pos, major.tag);

          if (pos === start) {

            /* get the screen project of the worldspace first position */

            [xOff, dummy] = this._viewer.worldToScreen(psec / 3600.0 * vm._timeScale, 0);
          }

          const x: number = (pos * scale) - (start * scale) + xOff - left;

          if (pos < 0) {

            /* we don't have to draw anything before 0, time didn't exist :) */

            continue;
          }

          /* figure out the tick label ... */

          let n: number = 0;
          let s: string = '';

          switch (major.minor) {

            case TimeScaleMode.HALFHOUR: {
              n = Math.floor(psec / 1800);
              s = ' half hour';
              break;
            }

            case TimeScaleMode.HOUR: {
              n = Math.floor(psec / 3600);
              s = ' hour';
              break;
            }

            case TimeScaleMode.DAY: {
              n = Math.floor(psec / 86400);
              s = ' day';
              break;
            }

            case TimeScaleMode.WEEK: {
              n = Math.floor(psec / 604800);
              s = ' week';
              break;
            }

            case TimeScaleMode.MONTH: {
              n = Math.floor(psec / 2419200);
              s = ' month';
              break;
            }
          }

          const label = n.toString().padStart(2, '0') + s;

          /*
          let timeofday:string = new Date(psec * 1000).toISOString().substr(11, 8);
          let       day:string = Math.floor(psec / 86400).toString().padStart(2, '0');

          let     label:string = 'D ' + day + ' ' + timeofday;
          */

          /* draw the label */

          context.font         = 'normal bold ' + fs + 'px Verdana';
          context.textBaseline = 'middle';

          if (this._nightMode) {

            context.strokeStyle = this._prefs.axis.night.fontColor;
            context.fillStyle   = this._prefs.axis.night.fontColor;

          } else {

            context.strokeStyle = this._prefs.axis.day.fontColor;
            context.fillStyle   = this._prefs.axis.day.fontColor;
          }

          context.textBaseline = 'bottom';
          context.textAlign    = 'left';

          context.fillText(label, x + 4, yMajor - 4);

        }
      }

    } /* end of major ticks */

    this._cursors.update();
  }

  /** @brief handle resizing of parent (and then our bounds) */
  resize(): void {

    this._width  = $(this._canvas).width();
    this._height = $(this._canvas).height();

    this._canvas.width  = this._width;
    this._canvas.height = this._height;

    Logger.debug('[time]', 'resized, w:', this._width, ' h:', this._height);

    this._cursors.resize();

    this.update();

  }

  /** @brief set the display mode (dark vs light) */
  setNightMode(flag: boolean) {

    if (flag) {

      $(this._canvas).removeClass('day');
      $(this._canvas).addClass('night');

    } else {

      $(this._canvas).removeClass('night');
      $(this._canvas).addClass('day');
    }

    this._nightMode = flag;
  }

}
